/**
 * Turning what the camera gave us into something worth uploading.
 *
 * A modern iPhone photo is 3–5 MB. Uploading that over a restaurant's wifi is
 * slow, and it would burn the free storage tier in a few hundred documents for
 * no benefit — the extraction service reads a 2000px JPEG exactly as well as a
 * 12-megapixel one.
 */

const MAX_EDGE = 2000
const QUALITY = 0.82

export interface CapturedPage {
  blob: Blob
  contentType: string
  /** What to call it on screen. Empty for a camera capture — it has no name. */
  name: string
  width: number
  height: number
  /** Rough blur score. Lower is blurrier. A PDF has no blur to measure. */
  sharpness: number | null
}

/** Azure reads a PDF directly, so there is nothing useful to do to one here. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024

/**
 * Anything a person might hand us: a camera capture, a photo from the library,
 * or a PDF that arrived by email and was saved to Files.
 *
 * A PDF is passed through untouched. Re-encoding it would throw away the text
 * layer, which is the one thing that makes a PDF invoice read perfectly —
 * turning a machine-readable document into a photograph of itself is the
 * opposite of helpful.
 */
export async function prepareFile(file: File): Promise<CapturedPage> {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(`${file.name || 'Dit bestand'} is te groot (max 20 MB).`)
  }

  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    return {
      blob: file,
      contentType: 'application/pdf',
      name: file.name || 'document.pdf',
      width: 0,
      height: 0,
      sharpness: null,
    }
  }

  if (!file.type.startsWith('image/')) {
    throw new Error(`${file.name || 'Dit bestand'} is geen foto of pdf.`)
  }

  const page = await preparePage(file)
  return { ...page, name: file.name || '' }
}

export async function preparePage(file: File): Promise<CapturedPage> {
  const bitmap = await createImageBitmap(file)

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
  const width = Math.round(bitmap.width * scale)
  const height = Math.round(bitmap.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('No 2D context')
  ctx.drawImage(bitmap, 0, 0, width, height)

  const sharpness = estimateSharpness(ctx, width, height)
  bitmap.close?.()

  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Encode failed'))), 'image/jpeg', QUALITY),
  )

  return { blob, contentType: 'image/jpeg', name: '', width, height, sharpness }
}

/**
 * Variance of a cheap Laplacian over a centre crop.
 *
 * Only ever used to *suggest* retaking a photo — never to block a send. A
 * parent who is told "no" by this app stops using it, and a blurry photo of an
 * invoice is still far more useful than no photo at all.
 */
function estimateSharpness(ctx: CanvasRenderingContext2D, w: number, h: number): number {
  const size = Math.min(320, w, h)
  const x = Math.floor((w - size) / 2)
  const y = Math.floor((h - size) / 2)
  const { data } = ctx.getImageData(x, y, size, size)

  const grey = new Float32Array(size * size)
  for (let i = 0; i < grey.length; i++) {
    const p = i * 4
    grey[i] = 0.299 * data[p]! + 0.587 * data[p + 1]! + 0.114 * data[p + 2]!
  }

  let sum = 0
  let sumSq = 0
  let n = 0
  for (let row = 1; row < size - 1; row++) {
    for (let col = 1; col < size - 1; col++) {
      const i = row * size + col
      const lap =
        4 * grey[i]! - grey[i - 1]! - grey[i + 1]! - grey[i - size]! - grey[i + size]!
      sum += lap
      sumSq += lap * lap
      n++
    }
  }
  if (n === 0) return 0
  const mean = sum / n
  return sumSq / n - mean * mean
}

export const BLUR_THRESHOLD = 90

export function looksBlurry(page: CapturedPage): boolean {
  // A PDF has no blur to measure, and telling someone their PDF looks fuzzy
  // would be both wrong and slightly insulting.
  return page.sharpness !== null && page.sharpness < BLUR_THRESHOLD
}
